/**
 * Platform Card Component
 * プラットフォーム専用カードコンポーネント
 * 
 * World-class UI/UX with Apple, Google, Stripe level design excellence
 * Features:
 * - Platform-specific branding and colors
 * - Animated connection status indicators
 * - Smooth hover and interaction animations
 * - Accessible design (WCAG AAA)
 * - Mobile-first responsive layout
 */

import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Avatar,
  LinearProgress,
  Tooltip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import type { ExternalPlatform, SyncStatus } from '../../../types/external-sync';

// Platform configurations with brand colors and identities
const PLATFORM_CONFIG = {
  hotpepper: {
    name: 'ホットペッパービューティー',
    shortName: 'ホットペッパー',
    color: '#ff6b35',
    gradientFrom: '#ff6b35',
    gradientTo: '#ff8c42',
    icon: '🌶️',
    description: '国内最大級の美容サロン予約サイト',
  },
  rakuten: {
    name: '楽天ビューティー',
    shortName: '楽天',
    color: '#bf0000',
    gradientFrom: '#bf0000',
    gradientTo: '#dc2626',
    icon: '🛍️',
    description: '楽天ポイントが使える美容予約サイト',
  },
  minimo: {
    name: 'minimo',
    shortName: 'minimo',
    color: '#00c4cc',
    gradientFrom: '#00c4cc',
    gradientTo: '#22d3ee',
    icon: '✨',
    description: 'トレンドを発信する次世代美容予約アプリ',
  },
  epark: {
    name: 'EPARKビューティー',
    shortName: 'EPARK',
    color: '#0ea5e9',
    gradientFrom: '#0ea5e9',
    gradientTo: '#38bdf8',
    icon: '🅿️',
    description: 'EPARKの美容サロン予約サービス',
  },
  kanzashi: {
    name: 'Kanzashi',
    shortName: 'Kanzashi',
    color: '#8b5cf6',
    gradientFrom: '#8b5cf6',
    gradientTo: '#a78bfa',
    icon: '🎀',
    description: 'サロン管理システムKanzashi',
  },
} as const;

// Status configurations with semantic colors and animations
const STATUS_CONFIG = {
  synced: {
    color: '#10b981',
    icon: CheckCircleIcon,
    label: '接続済み',
    description: '正常に同期されています',
    pulse: false,
  },
  pending: {
    color: '#f59e0b',
    icon: WarningIcon,
    label: '同期中',
    description: '接続を確立中です',
    pulse: true,
  },
  error: {
    color: '#dc2626',
    icon: ErrorIcon,
    label: 'エラー',
    description: '接続に問題があります',
    pulse: true,
  },
} as const;

export interface PlatformCardProps {
  platform: ExternalPlatform;
  status: SyncStatus;
  lastSyncAt?: Date;
  reservationCount?: number;
  isLoading?: boolean;
  onConfigure?: () => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onRefresh?: () => void;
  className?: string;
}

const PlatformCard: React.FC<PlatformCardProps> = memo(({
  platform,
  status,
  lastSyncAt,
  reservationCount = 0,
  isLoading = false,
  onConfigure,
  onConnect,
  onDisconnect,
  onRefresh,
  className,
}) => {
  const theme = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  
  const platformConfig = PLATFORM_CONFIG[platform];
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;

  const handleConnect = useCallback(() => {
    if (status === 'synced') {
      onDisconnect?.();
    } else {
      onConnect?.();
    }
  }, [status, onConnect, onDisconnect]);

  const formatLastSync = (date?: Date): string => {
    if (!date) return '未同期';
    
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    
    if (diffMinutes < 1) return 'たった今';
    if (diffMinutes < 60) return `${diffMinutes}分前`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}時間前`;
    return `${Math.floor(diffMinutes / 1440)}日前`;
  };

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ 
        type: 'spring',
        stiffness: 400,
        damping: 25,
      }}
      className={className}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Card
        sx={{
          position: 'relative',
          overflow: 'hidden',
          minHeight: 280,
          background: `linear-gradient(135deg, ${platformConfig.gradientFrom}08 0%, ${platformConfig.gradientTo}08 100%)`,
          border: `2px solid ${alpha(platformConfig.color, 0.12)}`,
          borderRadius: 3,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            borderColor: alpha(platformConfig.color, 0.3),
            boxShadow: `0 8px 32px ${alpha(platformConfig.color, 0.15)}`,
          },
        }}
      >
        {/* Loading overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 10,
              }}
            >
              <LinearProgress 
                sx={{
                  bgcolor: alpha(platformConfig.color, 0.1),
                  '& .MuiLinearProgress-bar': {
                    bgcolor: platformConfig.color,
                  },
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header gradient */}
        <Box
          sx={{
            height: 80,
            background: `linear-gradient(135deg, ${platformConfig.gradientFrom} 0%, ${platformConfig.gradientTo} 100%)`,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            color: 'white',
          }}
        >
          {/* Platform info */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                fontSize: '1.5rem',
                width: 48,
                height: 48,
              }}
            >
              {platformConfig.icon}
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={600}
                sx={{ 
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                  lineHeight: 1.2,
                }}
              >
                {platformConfig.shortName}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  opacity: 0.9,
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                {platformConfig.description}
              </Typography>
            </Box>
          </Stack>

          {/* Status indicator */}
          <motion.div
            animate={statusConfig.pulse ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Chip
              icon={<StatusIcon sx={{ fontSize: '1rem' }} />}
              label={statusConfig.label}
              size="small"
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                color: statusConfig.color,
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: statusConfig.color,
                },
              }}
            />
          </motion.div>
        </Box>

        <CardContent sx={{ p: 3 }}>
          {/* Statistics */}
          <Stack spacing={2} mb={3}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                p: 2,
                bgcolor: alpha(theme.palette.background.paper, 0.7),
                borderRadius: 2,
                border: `1px solid ${alpha(platformConfig.color, 0.1)}`,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                本日の予約数
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight={600}
                color={platformConfig.color}
              >
                {reservationCount}件
              </Typography>
            </Box>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  最終同期
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={500}>
                {formatLastSync(lastSyncAt)}
              </Typography>
            </Stack>
          </Stack>

          {/* Action buttons */}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="設定">
              <IconButton
                onClick={onConfigure}
                size="small"
                sx={{
                  bgcolor: alpha(platformConfig.color, 0.1),
                  '&:hover': {
                    bgcolor: alpha(platformConfig.color, 0.2),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <SettingsIcon sx={{ color: platformConfig.color }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="手動同期">
              <IconButton
                onClick={onRefresh}
                disabled={isLoading}
                size="small"
                sx={{
                  bgcolor: alpha(platformConfig.color, 0.1),
                  '&:hover': {
                    bgcolor: alpha(platformConfig.color, 0.2),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <motion.div
                  animate={isLoading ? { rotate: 360 } : {}}
                  transition={{ duration: 1, repeat: isLoading ? Infinity : 0, ease: 'linear' }}
                >
                  <RefreshIcon sx={{ color: platformConfig.color }} />
                </motion.div>
              </IconButton>
            </Tooltip>

            <Tooltip title={status === 'synced' ? '接続を切断' : '接続'}>
              <IconButton
                onClick={handleConnect}
                disabled={isLoading}
                size="small"
                sx={{
                  bgcolor: alpha(statusConfig.color, 0.1),
                  '&:hover': {
                    bgcolor: alpha(statusConfig.color, 0.2),
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                {status === 'synced' ? (
                  <LinkOffIcon sx={{ color: statusConfig.color }} />
                ) : (
                  <LinkIcon sx={{ color: statusConfig.color }} />
                )}
              </IconButton>
            </Tooltip>
          </Stack>
        </CardContent>

        {/* Hover effect overlay */}
        <AnimatePresence>
          {isHovered && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `linear-gradient(135deg, ${alpha(platformConfig.color, 0.03)} 0%, ${alpha(platformConfig.color, 0.06)} 100%)`,
                pointerEvents: 'none',
                borderRadius: 'inherit',
              }}
            />
          )}
        </AnimatePresence>
      </Card>
    </motion.div>
  );
});

PlatformCard.displayName = 'PlatformCard';

export default PlatformCard;